import { useAnimate } from "framer-motion"
import { Link, graphql, navigate, useStaticQuery } from "gatsby"
import parse from "html-react-parser"
import React, { useEffect, useState } from "react"
import "./Header.css"

function useScrollDirection() {
  const [scrollDirection, setScrollDirection] = React.useState(null)

  React.useEffect(() => {
    let lastScrollY = window.pageYOffset

    const updateScrollDirection = () => {
      const scrollY = window.pageYOffset
      const direction = scrollY > lastScrollY ? "down" : "up"
      if (
        direction !== scrollDirection &&
        (scrollY - lastScrollY > 1 || scrollY - lastScrollY < -1)
      ) {
        setScrollDirection(direction)
      }
      lastScrollY = scrollY > 0 ? scrollY : 0
    }
    window.addEventListener("scroll", updateScrollDirection) // add event listener

    return () => {
      window.removeEventListener("scroll", updateScrollDirection) // clean up
    }
  }, [scrollDirection])

  return scrollDirection
}
const Header = ({ location }) => {
  const scrollDirection = useScrollDirection()
  const [menuOpen, setMenuOpen] = useState(false)
  const [booktableOpen, setBooktableOpen] = useState(false)
  const [addClass, setAddClass] = useState(false)

  const [scope, animate] = useAnimate()
  const [searchtext, setSearchtext] = useState("")
  const [searchMenu, setSearchMenu] = useState(false)

  let params = {}
  if (typeof window !== "undefined") {
    params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    })
  }

  useEffect(() => {
    setSearchtext(params.q)
  }, [])

  useEffect(() => {
    const menuAnimations = menuOpen
      ? [
          [".main-nav", { transform: "translateX(0%)" }, { duration: 0.6 }],
          [".navbar-menu ul", { opacity: 0 }, { duration: 0.6, at: "-1" }],
          [
            ".navMenu",
            { transform: "translateX(0px)", opacity: 1 },
            { duration: 0.5, at: "4" },
          ],
        ]
      : [
          [
            ".navMenu",
            { transform: "translateX(50px)", opacity: 0 },
            { duration: 0.5, at: "1" },
          ],
          [".main-nav", { transform: "translateX(110%)" }, { duration: 0.6 }],
          [".navbar-menu ul", { opacity: 1 }, { duration: 0.6 }],
        ]
    animate([...menuAnimations])
  }, [scope, menuOpen, animate])

  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add("menu-open")
    } else {
      document.body.classList.remove("menu-open")
    }
  }, [menuOpen])

  // Book Table
  useEffect(() => {
    const bookAnimations = booktableOpen
      ? [
          [".book-table", { transform: "translateY(0%)" }, { duration: 0.6 }],
          [".navbar-menu ul", { opacity: 0 }, { duration: 0.6, at: "-0.5" }],

          [
            ".make-enquiry",
            { opacity: 1, visibility: "visible" },
            { duration: 0.6, at: "-0.5" },
          ],
          [
            ".btAnim",
            { transform: "translateY(0px)", opacity: 1 },
            { duration: 0.5, at: "4" },
          ],
        ]
      : [
          [
            ".btAnim",
            { transform: "translateY(50px)", opacity: 0 },
            { duration: 0.5, at: "1" },
          ],
          [
            ".book-table",
            { transform: "translateY(-100%)" },
            { duration: 0.6 },
          ],
          [".navbar-menu ul", { opacity: 1 }, { duration: 0.6, at: "-0.5" }],

          [
            ".make-enquiry",
            { opacity: 0, visibility: "hidden" },
            { duration: 0.00001, at: "-5" },
          ],
        ]
    animate([...bookAnimations])
  }, [booktableOpen, animate])

  useEffect(() => {
    if (booktableOpen) {
      document.body.classList.add("book-open")
    } else {
      document.body.classList.remove("book-open")
    }
  }, [booktableOpen])

  useEffect(() => {
    if (menuOpen) {
      const timeout = setTimeout(() => {
        setAddClass(true)
      }, 10)
      return () => clearTimeout(timeout)
    } else {
      const timeout = setTimeout(() => {
        setAddClass(false)
      }, 10)
      return () => clearTimeout(timeout)
    }
  }, [menuOpen])

  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector("header")
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop

      if (scrollTop === 0) {
        header.classList.remove("header-sticky")
      }
    }

    window.addEventListener("scroll", handleScroll)

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const MenuToggle = ({ toggle }) => (
    <div
      className={`navbar-icon ${addClass ? "nav-open" : ""}`}
      onClick={toggle}
      onKeyDown={toggle}
      aria-hidden="true"
    >
      <Link to="#" className="grid gap-y-1">
        <img
          src="../../../images/menu-icon.svg"
          width={21}
          height={12}
          alt="menu-icon"
          className="menu-icon toggle-line-open"
          loading="eager"
        />
      </Link>
      <Link to="#" className="grid gap-y-1">
        <img
          src="../../../images/close.svg"
          width={15}
          height={15}
          alt="close-icon"
          className="close-icon toggle-line-close"
          loading="eager"
        />
      </Link>
    </div>
  )

  const BookToggle = ({ booktoggle }) => (
    <div
      className="bt-icon"
      onClick={booktoggle}
      onKeyDown={booktoggle}
      aria-hidden="true"
    >
      {headerDataList?.bookWithUs && (
        <Link
          to={headerDataList.bookWithUs.url}
          className="btn btn-border block smscreen2:hidden"
        >
          {headerDataList.bookWithUs.title}
        </Link>
      )}
    </div>
  )

  const BookToggleMenu = ({ booktogglemenu }) => (
    <div
      className="bt-icon mt-20"
      onClick={booktogglemenu}
      onKeyDown={booktogglemenu}
      aria-hidden="true"
    >
      {headerDataList?.bookWithUs && (
        <Link
          to={headerDataList.bookWithUs.url}
          className="btn btn-black text-center"
        >
          {headerDataList.bookWithUs.title}
        </Link>
      )}
    </div>
  )

  const headerData = useStaticQuery(graphql`
    query {
      wp {
        fluxDnaSettings {
          fluxDna {
            mainLogo {
              altText
              mediaItemUrl
            }
            footerLogo {
              altText
              mediaItemUrl
            }
            mainMenu {
              menuLink {
                target
                title
                url
              }
              menuButton {
                target
                title
                url
              }
              subMenuItem {
                subMenuLink {
                  innerSubMenu {
                    target
                    title
                    url
                  }
                }
              }
            }
            bookWithUs {
              target
              title
              url
            }
            popupMenuMain {
              menu {
                target
                title
                url
              }
            }
            popupMenuSubOne {
              menu {
                target
                title
                url
              }
            }
            generalEnquiry {
              target
              title
              url
            }
            book {
              heading
              description
              button {
                target
                title
                url
              }
            }
          }
        }
      }
    }
  `)
  const headerDataList = headerData.wp.fluxDnaSettings.fluxDna

  return (
    <React.Fragment>
      <section ref={scope} className="header">
        <header
          className={`py-20 fixed w-full z-99 transition-all duration-500 ease-in-out bg-stone smscreen2:px-0 ${
            scrollDirection === "down" && !location?.state?.prevScrollPosition
              ? "-top-100 -z-9"
              : "top-0 z-9 "
          } ${
            scrollDirection === "up" && window.pageYOffset !== "0"
              ? "header-sticky "
              : ""
          }`}
        >
          <div className="container-fluid relative">
            <div className="flex flex-wrap items-center justify-between">
              {headerDataList?.mainLogo && (
                <div className="logo">
                  <Link to="/">
                    <img
                      src={headerDataList.mainLogo.mediaItemUrl}
                      loading="eager"
                      width={273}
                      height={20}
                      alt={headerDataList.mainLogo.altText}
                    />
                  </Link>
                </div>
              )}
              <div className="navbar-menu flex flex-wrap items-center gap-8">
                <ul className="flex flex-wrap gap-x-6 laptop:gap-x-6 lgscreen:hidden menuHover">
                  {headerDataList.mainMenu.length > 0 &&
                    headerDataList.mainMenu.map((menu_item, j) => {
                      return (
                        <li key={j}>
                          {menu_item?.menuLink && (
                            <Link
                              to={menu_item.menuLink.url}
                              target={menu_item.menuLink.target}
                              className="uppercase text-14 tracking-05em relative flex flex-wrap items-center"
                            >
                              {parse(menu_item.menuLink.title)}
                              {menu_item?.subMenuItem && (
                                <span className="relative top-0 ml-5">
                                  <img
                                    src="../../../images/menu-dropdown.svg"
                                    width="9"
                                    height="8"
                                    alt="menu-dropdown"
                                    loading="eager"
                                  />
                                </span>
                              )}
                            </Link>
                          )}
                          {menu_item?.subMenuItem && (
                            <div className="sub-menu grid grid-cols-2">
                              {menu_item.subMenuItem &&
                                menu_item.subMenuItem.map((sub_item, t) => {
                                  return (
                                    <ul key={t}>
                                      {sub_item.subMenuLink &&
                                        sub_item.subMenuLink.map(
                                          (sub_item, m) => {
                                            return (
                                              <li key={m}>
                                                <Link
                                                  to={sub_item.innerSubMenu.url}
                                                  target={
                                                    sub_item.innerSubMenu.target
                                                  }
                                                >
                                                  {parse(
                                                    sub_item.innerSubMenu.title
                                                  )}
                                                </Link>
                                              </li>
                                            )
                                          }
                                        )}
                                    </ul>
                                  )
                                })}
                              {menu_item?.menuButton && (
                                <div className="inline-block mt-20">
                                  <Link
                                    to={menu_item.menuButton.url}
                                    target={menu_item.menuButton.target}
                                    className="btn btn-border w-[139px] text-center"
                                  >
                                    {menu_item.menuButton.title}
                                  </Link>
                                </div>
                              )}
                            </div>
                          )}
                        </li>
                      )
                    })}
                </ul>
              </div>
              <div className="navbar-right flex flex-wrap items-center gap-x-5 smscreen2:gap-x-2">
                <div className="search">
                  <button
                    className="relative z-99 top-[4px]"
                    id="mobileSearchIcon"
                    onClick={() => {
                      setSearchMenu(!searchMenu)
                      setTimeout(() => {
                        document
                          .querySelector(".header-search__inner")
                          .classList.add("active")
                      }, 10)
                    }}
                  >
                    <img
                      src="/images/search-icon.svg"
                      loading="eager"
                      className="max-w-[20px]"
                      alt="Search"
                    />
                  </button>
                </div>

                <MenuToggle
                  toggle={() => {
                    setMenuOpen(!menuOpen)
                  }}
                  onClick={() => {
                    !menuOpen ? setMenuOpen(true) : setMenuOpen(false)
                  }}
                ></MenuToggle>

                {headerDataList?.generalEnquiry && (
                  <div className="make-enquiry absolute top-[12px] right-[80px] smscreen2:right-60">
                    <div className="flex flex-wrap gap-x-[5px]">
                      <img
                        src="../../../images/contact_support.svg"
                        width="11"
                        height="13"
                        alt="contact_support"
                        loading="eager"
                      />

                      <span className="font-ositalic text-13">
                        <Link
                          to={headerDataList.generalEnquiry.url}
                          target={headerDataList.generalEnquiry.target}
                        >
                          {headerDataList.generalEnquiry.title}
                        </Link>
                      </span>
                    </div>
                  </div>
                )}

                <BookToggle
                  booktoggle={() => {
                    setBooktableOpen(!booktableOpen)
                  }}
                  onClick={() => {
                    !booktableOpen
                      ? setBooktableOpen(true)
                      : setBooktableOpen(false)
                  }}
                  onKeyDown={() => {
                    !booktableOpen
                      ? setBooktableOpen(true)
                      : setBooktableOpen(false)
                  }}
                  aria-hidden="true"
                ></BookToggle>

                <div
                  className={`booking-close cursor-pointer absolute top-0 right-30 ${
                    booktableOpen === false ? "hidden" : ""
                  } `}
                  onClick={() => {
                    !booktableOpen
                      ? setBooktableOpen(true)
                      : setBooktableOpen(false)
                  }}
                  onKeyDown={() => {
                    !booktableOpen
                      ? setBooktableOpen(true)
                      : setBooktableOpen(false)
                  }}
                  aria-hidden="true"
                >
                  <img
                    src="../../../images/close.svg"
                    width="16"
                    height="16"
                    alt="close"
                    loading="eager"
                  />
                </div>

                <div
                  className="mobileBtnIcon hidden smscreen2:block border-1 border-charcoal rounded-2 w-[81px] h-[49px] xsscreen:w-[60px]"
                  onClick={() => {
                    !booktableOpen
                      ? setBooktableOpen(true)
                      : setBooktableOpen(false)
                  }}
                  onKeyDown={() => {
                    !booktableOpen
                      ? setBooktableOpen(true)
                      : setBooktableOpen(false)
                  }}
                  aria-hidden="true"
                >
                  <div className="h-full flex items-center justify-center">
                    <img
                      src="../../../images/room_service.svg"
                      alt="room_service"
                      width="20"
                      height="14"
                      loading="eager"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="main-nav">
          <div className="menu-overlay h-full fixed w-full top-0 left-0 bg-stone"></div>

          <div className="px-80 lgscreen:!px-80 xsscreen:!px-40 pt-90 smscreen2:pt-80 smscreen2:pb-50 h-full smscreen2:overflow-y-auto xlscreen:pl-80 xlscreen:pr-50 smscreen2:pr-0 relative overflow-y-auto pb-50">
            <div className="mainNav__col relative text-right">
              <div className="header-search-bx mb-30 mt-10 relative hidden mdscreen:block">
                <form
                  className="relative"
                  onSubmit={event => {
                    event.preventDefault()
                    if (searchtext) {
                      setSearchMenu(false)
                      navigate(encodeURI("/search?q=" + searchtext), {
                        state: searchtext,
                      })
                    }
                  }}
                >
                  <input
                    type="text"
                    placeholder="Looking for something?"
                    value={searchtext || ""}
                    onChange={e => {
                      setSearchtext(e.target.value)
                    }}
                    className="placeholder:text-white w-[745px] h-[50px] p-[10px] border-none !bg-white rounded-none"
                  />
                  <button
                    type="submit"
                    className="top-[15px] right-15 absolute hover:cursor-pointer"
                  >
                    {" "}
                    <img
                      onClick={e => {}}
                      src="/images/search-icon.svg"
                      loading="lazy"
                      className="max-w-[20px]"
                      alt="Search"
                    />
                  </button>
                </form>
              </div>
              {headerDataList?.popupMenuMain && (
                <ul className="navMenu hover-underline-solid text-white grid gap-y-2">
                  {headerDataList.popupMenuMain.map((item, i) => (
                    <li key={i}>
                      {item?.menu && (
                        <Link
                          to={item.menu.url}
                          target={item.menu.target}
                          className="uppercase text-[25px] text-charcoal tracking-05em relative hover:text-blush"
                        >
                          {parse(item.menu.title)}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {headerDataList?.popupMenuSubOne && (
                <div className="navMenu sub-navbar border-t-1 border-charcoal w-[220px] ml-auto pt-20 mt-20">
                  <ul className="grid gap-y-2">
                    {headerDataList.popupMenuSubOne.map((item, i) => (
                      <li key={i}>
                        {item?.menu && (
                          <Link
                            to={item.menu.url}
                            target={item.menu.target}
                            className="uppercase text-[20px] text-charcoal tracking-05em relative hover:text-blush"
                          >
                            {parse(item.menu.title)}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>

                  <BookToggleMenu
                    booktogglemenu={() => {
                      setBooktableOpen(!booktableOpen)
                      setMenuOpen(!menuOpen)
                    }}
                    onClick={() => {
                      !menuOpen ? setMenuOpen(true) : setMenuOpen(false)
                      !booktableOpen
                        ? setBooktableOpen(true)
                        : setBooktableOpen(false)
                    }}
                  ></BookToggleMenu>
                </div>
              )}
            </div>
          </div>
        </div>
        {headerDataList?.book && (
          <div className="book-table pt-[130px] lgscreen:pt-110">
            <div className="menu-overlay h-[450px] lgscreen:h-full fixed w-full top-0 left-0 bg-stone"></div>
            <div className="px-70 mdscreen:pb-25 laptop:px-35 relative lgscreen:overflow-auto lgscreen:pb-50 h-full">
              <div className="flex flex-wrap -mx-25 mdscreen:mx-0 gap-y-[25px] btAnim">
                {headerDataList.book.length > 0 &&
                  headerDataList.book.map((book, k) => {
                    return (
                      <div
                        className="lg:w-3/12 md:w-6/12 w-full px-25 laptop:px-15 xlscreen:px-10 mdscreen:px-0"
                        key={k}
                      >
                        <div className="border-1 border-charcoal rounded-2 py-45 text-center">
                          {book?.heading && <h5>{book.heading}</h5>}

                          {book?.button && (
                            <div className="mt-20 smscreen:px-20">
                              <Link
                                to={book.button.url}
                                target={book.button.target}
                                className="btn btn-black"
                              >
                                {book.button.title}
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          </div>
        )}

        <div className={`header-search ${!searchMenu ? "hidden" : "block"}`}>
          <div className="header-search__inner translate-y-[-10px] opacity-0 p-30">
            <div className="header-search__border border-1 border-charcoal rounded-2 relative p-60 mdscreen:p-20">
              <img
                onClick={() => {
                  document
                    .querySelector(".header-search__inner")
                    .classList.remove("active")
                  setTimeout(() => {
                    setSearchMenu(!searchMenu)
                  }, 300)
                }}
                src="/images/close.svg"
                loading="eager"
                className="max-w-[17px] ml-15 hs-close absolute z-[99999] top-20 right-20 hover:cursor-pointer"
                alt="Search"
              />
              <div className="w-[745px] m-auto text-center lgscreen:w-full lgscreen:px-20 mdscreen:px-0">
                <div className="content">
                  <p className="font-ositalic text-charcoal text-16">Search</p>
                </div>
                <div className="pt-20">
                  <h3>What can we help you with?</h3>
                </div>
                <div className="header-search-bx mt-30 relative">
                  <form
                    className="relative"
                    onSubmit={event => {
                      event.preventDefault()
                      if (searchtext) {
                        setSearchMenu(false)
                        navigate(encodeURI("/search?q=" + searchtext), {
                          state: searchtext,
                        })
                      }
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Looking for something?"
                      value={searchtext || ""}
                      onChange={e => {
                        setSearchtext(e.target.value)
                      }}
                      className="placeholder:text-white w-[745px] h-[50px] p-[10px] border-none !bg-white rounded-none"
                    />
                    <button
                      type="submit"
                      className="top-[15px] right-15 absolute hover:cursor-pointer"
                    >
                      {" "}
                      <img
                        onClick={e => {}}
                        src="/images/search-icon.svg"
                        loading="lazy"
                        className="max-w-[20px]"
                        alt="Search"
                      />
                    </button>
                  </form>
                </div>
              </div>
              <div className="flex justify-center align-center">
                <div className="pt-30 content w-[745px] text-left border-t-1 border-t-[rgba(0,0,0,0.2)] mt-35">
                  <p className="font-ositalic text-charcoal text-16">
                    Popular Searches
                  </p>
                  <ul className="grid grid-cols-3 gap-y-[7px] pt-20 mdscreen:flex mdscreen:flex-wrap mdscreen:gap-[20px]">
                    <li>
                      <Link to="/eat-drinks">Eat & Drink</Link>
                    </li>
                    <li>
                      <Link to="/careers">Careers</Link>
                    </li>
                    <li>
                      <Link to="/whats-on">What's On</Link>
                    </li>
                    <li>
                      <Link to="/stay">Stay</Link>
                    </li>
                    <li>
                      <Link to="/contact">Contact Us</Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </React.Fragment>
  )
}

export default Header
